import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        'super-admin':
          'border-transparent bg-[hsl(var(--role-super-admin))] text-white [a&]:hover:bg-[hsl(var(--role-super-admin)/0.9)]',
        corporate:
          'border-transparent bg-[hsl(var(--role-corporate))] text-white [a&]:hover:bg-[hsl(var(--role-corporate)/0.9)]',
        admin:
          'border-transparent bg-[hsl(var(--role-admin))] text-white [a&]:hover:bg-[hsl(var(--role-admin)/0.9)]',
        management:
          'border-transparent bg-[hsl(var(--role-management))] text-white [a&]:hover:bg-[hsl(var(--role-management)/0.9)]',
        tenant:
          'border-transparent bg-[hsl(var(--role-tenant))] text-white [a&]:hover:bg-[hsl(var(--role-tenant)/0.9)]',
        success:
          'border-transparent bg-success text-success-foreground [a&]:hover:bg-success/90',
        warning:
          'border-transparent bg-warning text-warning-foreground [a&]:hover:bg-warning/90',
        'warning-outline': 'border-warning/50 text-warning',
        info: 'border-transparent bg-info text-info-foreground [a&]:hover:bg-info/90',
        // Fixed category accent (equipment-setup scan), intentionally not a
        // semantic-status token — same bucket as the role colors below.
        violet:
          'border-transparent bg-violet-500 text-white [a&]:hover:bg-violet-500/90',
        // Soft chip (occupied/active property) — tokenized soft-success.
        grass: 'border-transparent bg-success/15 text-success',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants }
